import {
  createContext,
  KeyboardEvent,
  PointerEvent,
  RefObject,
  TouchEvent,
  useCallback,
  useRef,
  WheelEvent,
} from 'react';

/** How close to the bottom (px) still counts as "reading the latest". */
export const FollowBottomThreshold = 120;

export interface ScrollMetrics {
  scrollTop: number;
  clientHeight: number;
  scrollHeight: number;
}

export function isNearBottom(
  { scrollTop, clientHeight, scrollHeight }: ScrollMetrics,
  threshold = FollowBottomThreshold
): boolean {
  return scrollTop + clientHeight >= scrollHeight - threshold;
}

/** Claude-style follow-scroll: keep the list pinned to the bottom while the
 *  reader is there. Only the reader's own upward scroll (wheel, touch,
 *  ArrowUp/PageUp/Home, or grabbing the scrollbar) pauses following — a scroll event by itself never does, because
 *  programmatic scrolls, clamps after content changes, and layout shifts all
 *  fire scroll events too, and any of them landing short of the bottom would
 *  otherwise silently stop the follow. Scrolling back near the bottom (or
 *  sending a message) resumes it.
 *
 *  Scrolls are instant — a smooth scroll restarted per revealed slice never
 *  finishes its animation and rubber-bands — and go through `scrollTo` with
 *  an explicit behavior, because a plain `scrollTop` assignment would honor
 *  the container's CSS `scroll-behavior: smooth`. */
const PauseKeys = new Set(['ArrowUp', 'PageUp', 'Home']);
/** Downward finger travel before a touch counts as scrolling up, not a tap. */
export const TouchDragThresholdPx = 10;
/** Upward wheel travel (summed within the window) before it counts as scrolling up. */
export const WheelUpThresholdPx = 8;
export const WheelUpWindowMs = 300;

export function useFollowBottom(scrollRef: RefObject<HTMLElement | null>) {
  const followingRef = useRef(true);
  const touchStartYRef = useRef<number | null>(null);

  const onScroll = useCallback(() => {
    const element = scrollRef.current;
    if (element && isNearBottom(element)) {
      followingRef.current = true;
    }
  }, [scrollRef]);

  // Trackpads emit tiny stray negative deltas mid-gesture; only upward wheel
  // travel that adds up within a short window counts as scrolling up.
  const wheelUpRef = useRef({ accumulated: 0, at: 0 });
  const onWheel = useCallback((event: WheelEvent<HTMLElement>) => {
    if (event.deltaY >= 0) {
      return;
    }
    const wheelUp = wheelUpRef.current;
    if (event.timeStamp - wheelUp.at > WheelUpWindowMs) {
      wheelUp.accumulated = 0;
    }
    wheelUp.accumulated += -event.deltaY;
    wheelUp.at = event.timeStamp;
    if (wheelUp.accumulated >= WheelUpThresholdPx) {
      followingRef.current = false;
    }
  }, []);

  const onTouchStart = useCallback((event: TouchEvent<HTMLElement>) => {
    touchStartYRef.current = event.touches[0]?.clientY ?? null;
  }, []);

  const onTouchMove = useCallback((event: TouchEvent<HTMLElement>) => {
    const startY = touchStartYRef.current;
    const currentY = event.touches[0]?.clientY;
    // Finger moving down the screen scrolls the content up. A tap wobbles by
    // a pixel or two, so only a real drag counts.
    if (startY !== null && currentY !== undefined && currentY - startY > TouchDragThresholdPx) {
      followingRef.current = false;
    }
  }, []);

  const onKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (PauseKeys.has(event.key)) {
      followingRef.current = false;
    }
  }, []);

  // A press on the vertical scrollbar (thumb or track) is a scroll intent the
  // wheel/touch handlers never see.
  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const element = scrollRef.current;
      if (!element) {
        return;
      }
      const scrollbarWidth = element.offsetWidth - element.clientWidth;
      if (scrollbarWidth > 0 && event.clientX >= element.getBoundingClientRect().right - scrollbarWidth) {
        followingRef.current = false;
      }
    },
    [scrollRef]
  );

  const resumeFollowing = useCallback(() => {
    followingRef.current = true;
  }, []);

  const followBottom = useCallback(() => {
    const element = scrollRef.current;
    if (element && followingRef.current) {
      element.scrollTo({ top: element.scrollHeight, behavior: 'instant' });
    }
  }, [scrollRef]);

  return {
    onScroll,
    onWheel,
    onTouchStart,
    onTouchMove,
    onKeyDown,
    onPointerDown,
    followBottom,
    resumeFollowing,
  };
}

/** Lets a streaming bubble ask the list to follow after each slice commits to
 *  the DOM. Default is a no-op so BotMessage renders fine outside the list. */
export const ChatFollowBottomContext = createContext<() => void>(() => {});
